import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Search, Ban, DollarSign, Eye, AlertTriangle, Trash2, Edit } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newPlan, setNewPlan] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: '',
    subscription_status: 'trial',
    subscription_plan: ''
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('parents'); // 'parents' or 'students'
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAdminAuth();
    loadUsers();
    loadChildren();
  }, []);

  const checkAdminAuth = () => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
    }
  };

  const loadUsers = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      
      if (!adminToken) {
        console.error('No admin token found');
        toast({
          title: 'Authentication Error',
          description: 'No admin token found. Please log in again.',
          variant: 'destructive'
        });
        navigate('/admin/login');
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      console.log('Fetching users from:', `${backendUrl}/api/admin/users`);
      
      const response = await fetch(`${backendUrl}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to load users');
      }

      const data = await response.json();
      console.log('Loaded users:', data.length);
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load user accounts',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadChildren = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/admin/children`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error loading children:', errorData);
        throw new Error(errorData.detail || 'Failed to load children');
      }

      const data = await response.json();
      setChildren(data);
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const loadUserDetails = async (userId) => {
    try {
      setActionLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load user details');
      }

      const data = await response.json();
      setSelectedUser(data);
      setIsDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load user details:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load user details',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(
        `${backendUrl}/api/admin/users/${selectedUser.user.id}/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel subscription');
      }

      const result = await response.json();
      
      toast({
        title: 'Success',
        description: result.message
      });

      setIsCancelDialogOpen(false);
      setIsDetailDialogOpen(false);
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel subscription',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(
        `${backendUrl}/api/admin/users/${selectedUser.user.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }

      const result = await response.json();
      
      toast({
        title: 'Success',
        description: `User account deleted. ${result.deleted_children} children removed.`
      });

      setIsDeleteDialogOpen(false);
      setIsDetailDialogOpen(false);
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete user',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedUser || !newStatus) return;

    try {
      setActionLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(
        `${backendUrl}/api/admin/users/${selectedUser.user.id}/status?status=${newStatus}${newPlan ? `&plan=${newPlan}` : ''}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update status');
      }

      const result = await response.json();
      
      toast({
        title: 'Success',
        description: result.message
      });

      setIsStatusDialogOpen(false);
      setIsDetailDialogOpen(false);
      setNewStatus('');
      setNewPlan('');
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update status',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateUser = async () => {
    // Validate inputs
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    try {
      setActionLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(
        `${backendUrl}/api/admin/users`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newUser)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create user');
      }

      const result = await response.json();
      
      toast({
        title: 'Success',
        description: `User account created for ${result.user.email}`
      });

      setIsCreateDialogOpen(false);
      setNewUser({
        email: '',
        name: '',
        password: '',
        subscription_status: 'trial',
        subscription_plan: ''
      });
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to create user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create user',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };



  const handleRefundSubscription = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(
        `${backendUrl}/api/admin/users/${selectedUser.user.id}/refund-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to refund subscription');
      }

      const result = await response.json();
      
      toast({
        title: 'Success',
        description: `${result.message}. Refund amount: $${result.refund_amount}`
      });

      setIsRefundDialogOpen(false);
      setIsDetailDialogOpen(false);
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to refund subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to refund subscription',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'active': 'bg-green-100 text-green-700',
      'trial': 'bg-blue-100 text-blue-700',
      'cancelled': 'bg-red-100 text-red-700',
      'expired': 'bg-gray-100 text-gray-700'
    };
    return variants[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredChildren = children.filter(child =>
    child.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    child.parent_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-slate-600">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">User Management</h2>
            <p className="text-slate-600">View and manage parent and student accounts</p>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Users className="w-4 h-4 mr-2" />
            Create User
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'parents' ? 'default' : 'outline'}
            onClick={() => setActiveTab('parents')}
          >
            <Users className="w-4 h-4 mr-2" />
            Parent Accounts ({users.length})
          </Button>
          <Button
            variant={activeTab === 'students' ? 'default' : 'outline'}
            onClick={() => setActiveTab('students')}
          >
            <Users className="w-4 h-4 mr-2" />
            Student Accounts ({children.length})
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder={activeTab === 'parents' ? "Search by email or name..." : "Search by student or parent name..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Parent Accounts Table */}
        {activeTab === 'parents' && (
          <Card>
            <CardHeader>
              <CardTitle>Parent Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-left p-3">Children</th>
                      <th className="text-left p-3">Created</th>
                      <th className="text-left p-3">Last Login</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-slate-50">
                        <td className="p-3 text-sm">{user.email}</td>
                        <td className="p-3 text-sm font-medium">{user.name}</td>
                        <td className="p-3">
                          <Badge className={getStatusBadge(user.subscription_status)}>
                            {user.subscription_status}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm">{user.subscription_plan || 'None'}</td>
                        <td className="p-3 text-sm">{user.children_count}</td>
                        <td className="p-3 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                        <td className="p-3 text-sm">
                          {user.last_login ? (
                            <span title={new Date(user.last_login).toLocaleString()}>
                              {new Date(user.last_login).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-slate-400">Never</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => loadUserDetails(user.id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Student Accounts Table */}
        {activeTab === 'students' && (
          <Card>
            <CardHeader>
              <CardTitle>Student Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Grade</th>
                      <th className="text-left p-3">Parent Email</th>
                      <th className="text-left p-3">Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChildren.map((child) => (
                      <tr key={child.id} className="border-b hover:bg-slate-50">
                        <td className="p-3 text-sm font-medium">{child.name}</td>
                        <td className="p-3 text-sm">{child.grade || 'N/A'}</td>
                        <td className="p-3 text-sm">{child.parent_email}</td>
                        <td className="p-3 text-sm">{child.tokens}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* User Details Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-medium">{selectedUser.user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Name</p>
                  <p className="font-medium">{selectedUser.user.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Status</p>
                  <Badge className={getStatusBadge(selectedUser.user.subscription_status)}>
                    {selectedUser.user.subscription_status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Plan</p>
                  <p className="font-medium">{selectedUser.user.subscription_plan || 'None'}</p>
                </div>
              </div>

              {selectedUser.subscription_details && !selectedUser.subscription_details.error && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Subscription Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-600">Status</p>
                      <p className="font-medium">{selectedUser.subscription_details.status}</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Amount</p>
                      <p className="font-medium">${selectedUser.subscription_details.amount}</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Current Period End</p>
                      <p className="font-medium">
                        {new Date(selectedUser.subscription_details.current_period_end).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-600">Auto-Renew</p>
                      <p className="font-medium">
                        {selectedUser.subscription_details.cancel_at_period_end ? 'No' : 'Yes'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedUser.children && selectedUser.children.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Children ({selectedUser.children.length})</h3>
                  <div className="space-y-2">
                    {selectedUser.children.map((child) => (
                      <div key={child.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <span>{child.name}</span>
                        <Badge variant="outline">{child.grade || 'N/A'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedUser.user.subscription_id && selectedUser.user.subscription_status !== 'cancelled' && (
                <div className="border-t pt-4 flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsCancelDialogOpen(true)}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => setIsRefundDialogOpen(true)}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Refund & Cancel
                  </Button>
                </div>
              )}

              {/* Admin Actions */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setNewStatus(selectedUser.user.subscription_status);
                      setIsStatusDialogOpen(true);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Change Status
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Subscription Status</DialogTitle>
            <DialogDescription>
              Change the user's subscription status (e.g., upgrade beta testers from trial to active).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newStatus === 'active' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Plan (Optional)</label>
                <Input
                  type="text"
                  placeholder="e.g., Beta Tester, Monthly, Annual"
                  value={newPlan}
                  onChange={(e) => setNewPlan(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus} disabled={actionLoading || !newStatus}>
              {actionLoading ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User Account
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the user account and ALL associated data including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All children profiles</li>
                <li>All assignments and grades</li>
                <li>All lesson plans</li>
                <li>All goals and rewards</li>
                <li>Stripe subscription (if exists)</li>
              </ul>
              <p className="mt-2 font-bold text-red-600">This action cannot be undone!</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              This will cancel the subscription at the end of the current billing period. The user will retain access until then.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleCancelSubscription} disabled={actionLoading}>
              {actionLoading ? 'Processing...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Confirmation Dialog */}
      <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Refund Subscription
            </DialogTitle>
            <DialogDescription>
              This will immediately cancel the subscription and refund the latest payment. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRefundDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRefundSubscription} disabled={actionLoading}>
              {actionLoading ? 'Processing...' : 'Confirm Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User Account</DialogTitle>
            <DialogDescription>
              Create a new parent account. Perfect for setting up beta testers or manually adding users.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Email *</label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Name *</label>
              <Input
                type="text"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Password *</label>
              <Input
                type="password"
                placeholder="Enter password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Subscription Status</label>
              <Select 
                value={newUser.subscription_status} 
                onValueChange={(value) => setNewUser({ ...newUser, subscription_status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial (14 days)</SelectItem>
                  <SelectItem value="active">Active (1 year for beta)</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newUser.subscription_status === 'active' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Plan Name (Optional)</label>
                <Input
                  type="text"
                  placeholder="e.g., Beta Tester"
                  value={newUser.subscription_plan}
                  onChange={(e) => setNewUser({ ...newUser, subscription_plan: e.target.value })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={actionLoading}>
              {actionLoading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
